import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, User as UserIcon, Camera, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE, BACKEND_URL } from '../config/api';

const API = API_BASE;

const Profile = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/users/profile`, { headers: getAuthHeaders() });
      setProfile(res.data);
      setEditName(res.data.name);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!editName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/users/profile`,
        { name: editName.trim() },
        { headers: getAuthHeaders() }
      );
      setProfile(res.data);
      updateUser(res.data);
      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
      return;
    }

    // Validate file size (1MB)
    const maxSize = 1048576; // 1MB in bytes
    if (file.size > maxSize) {
      toast.error(`File too large. Maximum size is 1MB. Your file is ${(file.size / 1048576).toFixed(2)}MB.`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // NOTE: Do NOT set Content-Type manually when sending FormData.
      // The browser must set it so the correct `boundary=...` parameter is included,
      // otherwise FastAPI's python-multipart parser cannot decode the body (→ 422).
      const res = await axios.post(`${API}/users/profile/avatar`, formData, {
        headers: getAuthHeaders(),
      });

      // Refresh profile to get new avatar
      await fetchProfile();
      updateUser({ ...profile, avatar_url: res.data.avatar_url });
      toast.success('Avatar updated successfully');
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload avatar';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAvatar = async () => {
    if (!window.confirm('Are you sure you want to remove your profile photo?')) {
      return;
    }

    try {
      await axios.delete(`${API}/users/profile/avatar`, { headers: getAuthHeaders() });
      await fetchProfile();
      updateUser({ ...profile, avatar_url: null });
      toast.success('Avatar removed successfully');
    } catch (error) {
      console.error('Failed to delete avatar:', error);
      toast.error('Failed to remove avatar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-destructive">Profile not found</div>
      </div>
    );
  }

  return (
    <div className="bg-background" data-testid="profile-page">
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12 max-w-2xl">
        <div className="flex items-center gap-2 md:gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            data-testid="back-button"
            className="-ml-2 flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="text-xs font-semibold tracking-wider uppercase">Back</span>
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-bold font-display tracking-tight">My Profile</h1>
            <p className="text-xs md:text-sm text-muted-foreground">Personal information</p>
          </div>
        </div>
        <Card className="p-6 md:p-8">
          {/* Avatar Section */}
          <div className="flex flex-col items-center mb-8">
            <div className="relative">
              {profile.avatar_url ? (
                <img
                  src={`${BACKEND_URL}${profile.avatar_url}`}
                  alt={profile.name}
                  className="h-32 w-32 rounded-full object-cover border-4 border-border"
                />
              ) : (
                <div className="h-32 w-32 rounded-full bg-primary flex items-center justify-center border-4 border-border">
                  <UserIcon className="h-16 w-16 text-primary-foreground" />
                </div>
              )}
              
              {!uploading && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 h-10 w-10 rounded-full bg-primary hover:bg-primary/90 flex items-center justify-center border-2 border-background transition-colors"
                  data-testid="change-avatar-button"
                >
                  <Camera className="h-5 w-5 text-primary-foreground" />
                </button>
              )}
              
              {uploading && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <div className="text-white text-sm">Uploading...</div>
                </div>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleAvatarUpload}
              className="hidden"
            />
            
            {profile.avatar_url && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDeleteAvatar}
                className="mt-3 text-destructive hover:text-destructive"
                data-testid="remove-avatar-button"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove photo
              </Button>
            )}
            
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Max file size: 1MB • JPEG, PNG, GIF, WebP
            </p>
          </div>

          {/* Profile Information */}
          <div className="space-y-6">
            {/* Name Field */}
            <div>
              <label className="text-sm font-medium mb-2 block">Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 rounded-sm border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your name"
                  data-testid="name-input"
                />
              ) : (
                <div className="text-base font-[Manrope]">{profile.name}</div>
              )}
            </div>

            {/* Email Field (Read-only) */}
            <div>
              <label className="text-sm font-medium mb-2 block">Email</label>
              <div className="text-base text-muted-foreground">{profile.email}</div>
              <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
            </div>

            {/* Member Since */}
            <div>
              <label className="text-sm font-medium mb-2 block">Member Since</label>
              <div className="text-base text-muted-foreground">
                {new Date(profile.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              {isEditing ? (
                <>
                  <Button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    data-testid="save-profile-button"
                    className="flex-1"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setEditName(profile.name);
                    }}
                    disabled={saving}
                    data-testid="cancel-edit-button"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setIsEditing(true)}
                  data-testid="edit-profile-button"
                  className="w-full"
                >
                  Edit Profile
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 p-4 bg-secondary/50">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> This is your personal profile. It represents who you are across all
            films in FrameFlow. Film-specific roles (director, editor, etc.) are managed within each
            film project.
          </p>
        </Card>
      </main>
    </div>
  );
};

export default Profile;
